import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import { isEmpty, isNil, find, get } from 'lodash'
import { IconBrandSlack, IconBrandTeams } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { Pill } from '@/components/pills/Pill'
import { RequiredIndicator } from '@/components/forms/RequiredIndicator'
import Select from '@/components/forms/Select'
import { FormField } from '@/components/forms/FormField'
import { TextInput } from '@/components/forms/TextInput'
import {
  fetchIntegrationCredentials,
  upsertIntegrationCredential,
  deactivateIntegrationCredential,
  testIntegrationCredential,
  getSlackOAuthUrl,
  getTeamsOAuthUrl,
} from '@/reducers/organizations/organization.actions'

// Slack and Teams both install via the Balto-owned app's consent flow rather
// than a hand-pasted credential: Slack's 'Add to Slack' OAuth and Teams'
// admin-consent redirect. Both land back here with ?slack= / ?teams= that
// OrganizationPage toasts. The manual field(s) stay as a fallback for another
// org (where the own-org consent redirect doesn't apply).
const SLACK_PROVIDER = 'slack_notifications'
const TEAMS_PROVIDER = 'teams_notifications'

const OAUTH_INSTALL = {
  [SLACK_PROVIDER]: {
    testidPrefix: 'slack',
    icon: <IconBrandSlack />,
    connectLabel: 'Add to Slack',
    reconnectLabel: 'Change workspace',
    getOAuthUrl: getSlackOAuthUrl,
    manualToggleLabel: 'Enter a token manually instead',
  },
  [TEAMS_PROVIDER]: {
    testidPrefix: 'teams',
    icon: <IconBrandTeams />,
    connectLabel: 'Connect Teams',
    reconnectLabel: 'Reconnect',
    getOAuthUrl: getTeamsOAuthUrl,
    manualToggleLabel: 'Enter a tenant ID manually instead',
  },
}

// Providers that support a connectivity test (Slack auth.test / Microsoft
// client_credentials token mint). Other providers don't have a cheap
// liveness check, so we hide the Test button for them.
const PROVIDERS_WITH_TEST = new Set(['slack_notifications', 'teams_notifications'])

const PROVIDER_OPTIONS = [
  { label: 'NICE CXone', value: 'nice_cx_one' },
  { label: 'HubSpot', value: 'hubspot' },
  { label: 'Slack (Notifications)', value: 'slack_notifications' },
  { label: 'Microsoft Teams (Notifications)', value: 'teams_notifications' },
  { label: 'Generic Webhook (Message Collector)', value: 'default' },
  { label: 'RingCX', value: 'ringcx' },
  { label: 'Genesys Cloud', value: 'genesys_cloud' },
]

// Field descriptors support `metadata: true` for non-secret provider settings:
// they're stored in the credential's metadata (not the encrypted blob), come
// back on GET so the form can prefill them, and an emptied value is an
// intentional clear rather than "leave unchanged".
const PROVIDER_FIELD_SCHEMAS = {
  nice_cx_one: [
    { name: 'access_key_id', label: 'Access Key ID', type: 'text' },
    { name: 'access_key_secret', label: 'Access Key Secret', type: 'password' },
    {
      name: 'ingestion_delay_seconds',
      label: 'Ingestion Delay (seconds)',
      type: 'number',
      optional: true,
      metadata: true,
      max: 86400,
      placeholder: '3600',
      help: 'Wait this many seconds after a conversation ends before fetching its transcript — for tenants where the CXone API exposes messages late. Leave blank to ingest immediately. Maximum 24 hours (86400).',
    },
  ],
  hubspot: [{ name: 'access_token', label: 'Access Token', type: 'password' }],
  default: [{ name: 'webhook_secret', label: 'Webhook Secret', type: 'password' }],
  ringcx: [
    { name: 'api_url', label: 'API URL', type: 'text', format: 'url' },
    { name: 'api_token', label: 'API Token', type: 'password' },
    { name: 'verify_token', label: 'Verify Token', type: 'password', optional: true },
    { name: 'webhook_secret', label: 'Webhook Secret', type: 'password', optional: true },
  ],
  genesys_cloud: [
    {
      name: 'client_id',
      label: 'Client ID',
      type: 'text',
      help: 'From the Genesys Cloud OAuth client (Admin → Integrations → OAuth) you created for Balto.',
    },
    {
      name: 'client_secret',
      label: 'Client Secret',
      type: 'password',
      help: 'The secret shown once at OAuth client creation. Regenerate from the same screen if lost.',
    },
    {
      name: 'region',
      label: 'Region',
      type: 'text',
      placeholder: 'mypurecloud.com',
      help: 'The Genesys Cloud login domain. Common values: mypurecloud.com (US East), usw2.pure.cloud (US West), mypurecloud.ie (EU Ireland), mypurecloud.de (EU Frankfurt), mypurecloud.com.au (AP Sydney).',
    },
  ],
  slack_notifications: [
    {
      name: 'bot_token',
      label: 'Bot User OAuth Token',
      type: 'password',
      placeholder: 'xoxb-...',
      help: 'From your Slack app: Features → OAuth & Permissions → "Bot User OAuth Token". Starts with xoxb-.',
    },
    {
      name: 'team_id',
      label: 'Workspace (Team) ID',
      type: 'text',
      placeholder: 'T01234567',
      help: 'From your Slack workspace: open Slack in the browser — the URL shows app.slack.com/client/<TEAM_ID>/...',
    },
  ],
  // Teams uses the single Balto-owned multi-tenant app (client id/secret are
  // Balto-wide env vars, not per-org), so the only per-org value is the
  // customer's tenant id. Connecting is normally the admin-consent redirect
  // below; this field is the manual fallback for another org.
  teams_notifications: [
    {
      name: 'tenant_id',
      label: 'Directory (Tenant) ID',
      type: 'text',
      placeholder: '00000000-0000-0000-0000-000000000000',
      help: "The customer tenant's Directory (tenant) ID (Entra ID → Overview). The Balto app must be admin-consented in that tenant.",
    },
  ],
}

const PROVIDER_SETUP_INSTRUCTIONS = {
  slack_notifications: {
    heading: 'Setting up Slack notifications',
    steps: [
      'Click “Add to Slack” below. You’ll be sent to Slack to choose the workspace to install the Balto app on.',
      'Review the requested permissions and approve. You’ll be redirected back here automatically.',
      'That’s it. Balto will DM managers directly, with no app creation, tokens, or channel setup required.',
    ],
    docsUrl: 'https://slack.com/help/articles/202035138-Add-apps-to-your-Slack-workspace',
  },
  teams_notifications: {
    heading: 'Setting up Microsoft Teams notifications',
    steps: [
      'Click “Connect Teams” below. A Microsoft admin-consent screen opens for your tenant.',
      'A Teams/Entra administrator approves the Balto app for the tenant. You’ll be redirected back here automatically.',
      'That’s it. Balto DMs managers directly — no app registration, client secret, or per-user setup required.',
    ],
    docsUrl: 'https://learn.microsoft.com/en-us/entra/identity/enterprise-apps/grant-admin-consent',
  },
}

// Manual Slack setup, shown when a Balto admin manages another org's
// credentials (the 'Add to Slack' OAuth install is own-org only).
const SLACK_MANUAL_INSTRUCTIONS = {
  heading: 'Connect Slack with a bot token',
  steps: [
    'In Slack, create an app at https://api.slack.com/apps (From scratch) on the target workspace.',
    'Under OAuth & Permissions, add the bot scopes chat:write, users:read, users:read.email, im:write, then install the app to the workspace.',
    'Copy the Bot User OAuth Token (xoxb-…) and the Workspace (Team) ID into the fields below.',
  ],
  docsUrl: 'https://api.slack.com/start/quickstart',
}

// Manual Teams setup, shown when a Balto admin manages another org's
// credentials (the admin-consent redirect is own-org only, and consent must be
// granted by an admin of the target org's own tenant).
const TEAMS_MANUAL_INSTRUCTIONS = {
  heading: 'Connect Teams with a tenant ID',
  steps: [
    'A Teams/Entra admin in the target tenant must admin-consent the Balto app — normally via the self-serve “Connect Teams” flow on their own org page.',
    'Once consented, paste that tenant’s Directory (Tenant) ID below to record it for this org.',
  ],
  docsUrl: 'https://learn.microsoft.com/en-us/entra/identity/enterprise-apps/grant-admin-consent',
}

const MANUAL_INSTRUCTIONS = {
  [SLACK_PROVIDER]: SLACK_MANUAL_INSTRUCTIONS,
  [TEAMS_PROVIDER]: TEAMS_MANUAL_INSTRUCTIONS,
}

const buildValidationSchema = (provider, isUpdate = false) => {
  const fields = PROVIDER_FIELD_SCHEMAS[provider] || []
  const shape = {}

  // On update, every field is optional in the form — the backend merges
  // blank/missing values with the existing encrypted blob so an unchanged
  // field stays unchanged. On create, the per-field `required` flag applies.
  fields.forEach((field) => {
    let schema
    if (field.type === 'number') {
      schema = Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? undefined : value))
        .typeError(`${field.label} must be a number`)
        .integer(`${field.label} must be a whole number`)
        .min(0, `${field.label} cannot be negative`)
      if (!isNil(field.max)) {
        schema = schema.max(field.max, `${field.label} cannot exceed ${field.max}`)
      }
    } else {
      schema = Yup.string()
      if (field.format === 'url') {
        schema = schema.url(`${field.label} must be a valid URL`)
      }
    }
    if (!field.optional && !isUpdate) {
      schema = schema.required(`${field.label} is required`)
    }
    shape[field.name] = schema
  })

  return Yup.object().shape(shape)
}

const buildInitialValues = (provider, existingCredential) => {
  const fields = PROVIDER_FIELD_SCHEMAS[provider] || []
  const values = {}

  fields.forEach((field) => {
    if (field.metadata) {
      const stored = get(existingCredential, ['metadata', field.name])
      values[field.name] = isNil(stored) ? '' : stored
    } else {
      values[field.name] = ''
    }
  })

  return values
}

export const IntegrationCredentialForm = ({ organizationId, onClose }) => {
  const dispatch = useDispatch()
  const credentials = useSelector((state) => state.organization.integrationCredentials)
  const userOrganizationId = useSelector((state) => state.currentUser.organizationid)
  // The consent redirect (Slack 'Add to Slack' OAuth / Teams admin-consent)
  // only makes sense for an admin acting on their own org — a Balto admin can't
  // consent on a customer's behalf. For any other org, fall back to manual
  // credential entry.
  const isOwnOrg = organizationId === userOrganizationId
  const [selectedProvider, setSelectedProvider] = useState(null)
  // {ok, detail} for the most recent test, or null when nothing's been tested.
  // Reset when the provider changes — a stale "connected" badge from another
  // provider would be misleading.
  const [testResult, setTestResult] = useState(null)
  const [testing, setTesting] = useState(false)
  // OAuth-install providers default to the consent redirect; the manual fields
  // stay hidden behind a fallback toggle.
  const [showManualEntry, setShowManualEntry] = useState(false)
  const [startingInstall, setStartingInstall] = useState(false)
  // Metadata fields prefill from the fetched credential, which makes Formik's
  // initialValues data-dependent — with enableReinitialize, a fetch resolving
  // mid-typing would reset the form and wipe entered secrets. Hold the form
  // back until the fetch has settled so initialValues are stable from mount.
  const [credentialsLoaded, setCredentialsLoaded] = useState(false)

  useEffect(() => {
    let active = true
    setCredentialsLoaded(false)
    dispatch(fetchIntegrationCredentials({ organizationId })).finally(() => {
      // Ignore a fetch that was superseded by an organizationId change or by
      // unmount, so a stale resolution can't flip the flag against newer state.
      if (active) {
        setCredentialsLoaded(true)
      }
    })
    return () => {
      active = false
    }
  }, [organizationId, dispatch])

  const existingCredential = find(credentials, { provider: selectedProvider })
  const fields = PROVIDER_FIELD_SCHEMAS[selectedProvider] || []
  const supportsTest = PROVIDERS_WITH_TEST.has(selectedProvider)
  // Slack/Teams offer a consent-redirect install, but only on the own org.
  const installConfig = isOwnOrg ? OAUTH_INSTALL[selectedProvider] : null
  // On another org there's no consent button, so show the manual setup steps
  // instead of the consent-flavored ones.
  const instructions =
    !isOwnOrg && MANUAL_INSTRUCTIONS[selectedProvider]
      ? MANUAL_INSTRUCTIONS[selectedProvider]
      : PROVIDER_SETUP_INSTRUCTIONS[selectedProvider]
  // For an OAuth-install provider on the own org, the credential form is the
  // fallback path — render it only once the user opts into manual entry. Every
  // other case (other providers, or another org where consent isn't offered)
  // shows it directly.
  const showCredentialForm =
    credentialsLoaded && !isEmpty(fields) && (!installConfig || showManualEntry)

  const handleProviderChange = (option) => {
    setSelectedProvider(option ? option.value : null)
    setTestResult(null)
    setShowManualEntry(false)
    // Reset the shared install flag so a prior provider's in-flight/failed
    // consent click can't leave the next provider's Connect button disabled.
    setStartingInstall(false)
  }

  const handleOAuthInstall = async () => {
    setStartingInstall(true)
    const url = await dispatch(
      installConfig.getOAuthUrl({ organizationId, returnTo: window.location.pathname })
    )
    if (url) {
      // Full-page redirect to the provider's consent screen; the callback
      // returns the browser to this org's page with ?slack= / ?teams=.
      window.location.assign(url)
      return
    }
    setStartingInstall(false)
  }

  const handleSubmit = (values) => {
    // Metadata fields are non-secret and prefilled, so unlike credentials an
    // emptied value is an intentional clear — sent as null, not omitted.
    const metadata = {}
    fields
      .filter((field) => field.metadata)
      .forEach((field) => {
        const value = values[field.name]
        if (value === '' || isNil(value)) {
          metadata[field.name] = null
        } else {
          metadata[field.name] = field.type === 'number' ? Number(value) : value
        }
      })
    // Drop empty credential values so the backend can distinguish "user
    // didn't touch this field" from "user wants to clear this field." The
    // form starts every secret field blank on edit (it can't pre-fill
    // secrets), so leaving one blank should preserve the existing value.
    const submitCredentials = Object.fromEntries(
      Object.entries(values).filter(([name, value]) => value !== '' && !(name in metadata))
    )
    dispatch(
      upsertIntegrationCredential({
        organizationId,
        provider: selectedProvider,
        credentials: submitCredentials,
        metadata,
        onSuccess: onClose,
      })
    )
  }

  const handleDeactivate = () => {
    dispatch(
      deactivateIntegrationCredential({
        organizationId,
        provider: selectedProvider,
      })
    )
  }

  const handleTest = async (values) => {
    setTesting(true)
    const result = await dispatch(
      testIntegrationCredential({
        organizationId,
        provider: selectedProvider,
        credentials: values,
      })
    )
    setTestResult(result || { ok: false, detail: 'No response' })
    setTesting(false)
  }

  return (
    <div>
      <FormField>
        <label>Provider</label>
        <Select
          data-testid="provider-select"
          options={PROVIDER_OPTIONS}
          value={selectedProvider}
          onChange={handleProviderChange}
          placeholder="Select a provider..."
          isClearable={false}
        />
      </FormField>

      {selectedProvider && existingCredential && (
        <div className="flex-align-center flex-space-between" style={{ margin: '0.5rem 0 1rem' }}>
          <Pill small success={existingCredential.active} outlined={!existingCredential.active}>
            {existingCredential.active ? 'Active' : 'Inactive'}
          </Pill>
          {existingCredential.last_updated && (
            <span className="small-text">
              Last updated:{' '}
              {new Date(existingCredential.last_updated).toLocaleDateString('en-US', {
                timeZone: 'UTC',
              })}
            </span>
          )}
        </div>
      )}

      {selectedProvider && instructions && (
        <div className="integration-setup-instructions" data-testid="setup-instructions">
          <strong>{instructions.heading}</strong>
          <ol>
            {instructions.steps.map((step) => (
              <li key={step} className="small-text">
                {step}
              </li>
            ))}
          </ol>
          {instructions.docsUrl && (
            <a
              href={instructions.docsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="small-text"
            >
              View official docs ↗
            </a>
          )}
        </div>
      )}

      {installConfig && (
        <div
          className="integration-oauth-install flex-align-center small-gap"
          data-testid={`${installConfig.testidPrefix}-install`}
        >
          <Button brand onClick={handleOAuthInstall} disabled={startingInstall}>
            {installConfig.icon}
            {existingCredential && existingCredential.active
              ? installConfig.reconnectLabel
              : installConfig.connectLabel}
          </Button>
          {!showManualEntry && (
            <Button
              basic
              onClick={() => setShowManualEntry(true)}
              data-testid={`${installConfig.testidPrefix}-manual-toggle`}
            >
              {installConfig.manualToggleLabel}
            </Button>
          )}
        </div>
      )}

      {showCredentialForm && (
        <Formik
          initialValues={buildInitialValues(selectedProvider, existingCredential)}
          validationSchema={buildValidationSchema(selectedProvider, !!existingCredential)}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting, dirty, isValid, values }) => (
            <Form className="field-stack mt">
              {fields.map((field) => (
                <FormField key={field.name}>
                  <label htmlFor={field.name} style={{ display: 'flex' }}>
                    {field.label}
                    {!field.optional && <RequiredIndicator />}
                  </label>
                  <Field
                    as={TextInput}
                    id={field.name}
                    name={field.name}
                    type={field.type}
                    placeholder={
                      // Secrets can't be prefilled, so dots stand in for the
                      // stored value on edit; metadata fields ARE prefilled.
                      existingCredential && !field.metadata
                        ? '••••••••'
                        : field.placeholder || `Enter ${field.label}`
                    }
                    data-testid={`credential-${field.name}`}
                  />
                  {field.help && <div className="field-help small-text">{field.help}</div>}
                </FormField>
              ))}

              {testResult && (
                <div className="integration-test-result small-text" data-testid="test-result">
                  <Pill small success={testResult.ok} critical={!testResult.ok}>
                    {testResult.ok ? 'Connected' : 'Failed'}
                  </Pill>
                  {testResult.detail && (
                    <span className="integration-test-result__detail">{testResult.detail}</span>
                  )}
                </div>
              )}

              <div className="form-actions is-modal">
                {existingCredential && existingCredential.active && (
                  <Button
                    type="button"
                    critical
                    onClick={handleDeactivate}
                    data-testid="deactivate-credential-button"
                  >
                    Deactivate
                  </Button>
                )}
                {supportsTest && (
                  <Button
                    type="button"
                    onClick={() => handleTest(values)}
                    disabled={testing || !isValid}
                    data-testid="test-credential-button"
                  >
                    {testing ? 'Testing…' : 'Test connection'}
                  </Button>
                )}
                <Button type="button" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  brand
                  disabled={isSubmitting || !dirty || !isValid}
                  disabledTooltip="All fields are required"
                  data-testid="save-credential-button"
                >
                  Save
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </div>
  )
}
